import React, { useMemo, useState } from 'react'
import { motion } from 'motion/react'

import { ConnectionData, EffectBackgroundProps, NodeData } from '../types'

export const NeuralNetworkEffect = React.memo(({ layerStyle, index, values }: EffectBackgroundProps) => {
  const effectColors = useMemo(() => values.effectColors || ['#6366f1'], [values.effectColors])
  const effectSpeed = values.effectSpeed ?? 100
  const effectOpacity = values.effectOpacity ?? 100
  const effectBackgroundColor = values.effectBackgroundColor || '#000000'
  const opacityValue = effectOpacity / 100

  const nodeColor = effectColors[0] || '#6366f1'
  const lineColor = effectColors[1] || effectColors[0] || '#6366f1'

  const [nodes] = useState(() => {
    const nodeCount = 20
    const newNodes: Array<NodeData> = []
    for (let i = 0; i < nodeCount; i++) {
      newNodes.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
      })
    }
    return newNodes
  })

  const [connections] = useState(() => {
    const newConnections: Array<ConnectionData> = []
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const distance = Math.sqrt(Math.pow(nodes[i].x - nodes[j].x, 2) + Math.pow(nodes[i].y - nodes[j].y, 2))
        if (distance < 30) {
          newConnections.push({
            from: nodes[i],
            to: nodes[j],
            delay: Math.random() * 2,
          })
        }
      }
    }
    return newConnections
  })

  return (
    <div
      style={{
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -index - 1,
        overflow: 'hidden',
        backgroundColor: effectBackgroundColor,
      }}
    >
      <svg className='absolute inset-0 w-full h-full'>
        {connections.map((conn, connIndex) => (
          <motion.line
            key={connIndex}
            x1={`${conn.from.x}%`}
            y1={`${conn.from.y}%`}
            x2={`${conn.to.x}%`}
            y2={`${conn.to.y}%`}
            stroke={lineColor}
            strokeWidth='1'
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, opacityValue, 0] }}
            transition={{
              duration: 3 * (100 / Math.max(effectSpeed, 1)),
              repeat: Infinity,
              ease: 'easeInOut',
              delay: conn.delay,
            }}
          />
        ))}
      </svg>

      {nodes.map((node) => (
        <motion.div
          key={node.id}
          style={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            width: '8px',
            height: '8px',
            backgroundColor: nodeColor,
            boxShadow: `0 0 10px ${nodeColor}`,
            opacity: opacityValue,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          animate={{
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 2 * (100 / Math.max(effectSpeed, 1)),
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  )
})
NeuralNetworkEffect.displayName = 'NeuralNetworkEffect'
